import type { LifeProfile } from '../types/life'
import { horizonOf } from './lifeGrid'
import { plural } from './format'

export interface RemainingCounter {
  id: string
  value: number
  label: string
  /** loss — not something still to come, but something taken out of what's left */
  tone?: 'loss'
}

const DAY_MS = 86_400_000
const YEAR_DAYS = 365.2425
const SLEEP_SHARE = 1 / 3

/**
 * "How many more times" counters — derived from date of birth and horizon only,
 * so they stay relevant to any adult without a single extra question.
 *
 * Two selection rules:
 * 1. Different cadences (year / week / day) — otherwise every number comes out
 *    the same and the punch is gone.
 * 2. The unit has to be bodily: something you do yourself, not something that
 *    happens in the sky. "Mornings" and "Friday nights" land, "full moons" don't.
 */
export function remainingCounters(profile: LifeProfile, now: Date): RemainingCounter[] {
  const days = Math.max(0, (horizonOf(profile).getTime() - now.getTime()) / DAY_MS)
  const years = days / YEAR_DAYS
  const weeks = Math.floor(days / 7)

  const birthdays = Math.floor(years)
  const fridays = weeks
  const weekends = weeks * 2
  const mornings = Math.floor(days)
  // в неделях, чтобы совпадать с основной единицей всего экрана
  const sleep = Math.floor(weeks * SLEEP_SHARE)

  return [
    { id: 'birthdays', value: birthdays, label: plural(birthdays, 'birthday') },
    { id: 'fridays', value: fridays, label: `Friday ${plural(fridays, 'night')}` },
    { id: 'weekends', value: weekends, label: `weekend ${plural(weekends, 'day')}` },
    { id: 'mornings', value: mornings, label: plural(mornings, 'morning') },
    { id: 'sleep', value: sleep, label: `${plural(sleep, 'week')} of it is sleep`, tone: 'loss' },
  ]
}
